/**
 * DraggablePin — shared pin component for the visual editor canvas.
 *
 * Handles drag-to-place and click-to-select via pointer capture. Used by
 * defect, BOM, and photo tabs; the design tab uses StudioMarker instead
 * (glyph-shaped markers with different drag semantics).
 *
 * Extracted from visual-style-file-page.tsx for reuse across tabs.
 */
import { useState } from "react";

/** Clamp a percentage value to [0, 100]. */
export const clamp = (n: number) => Math.min(100, Math.max(0, n));

export function DraggablePin({
  x,
  y,
  label,
  dotClass,
  ringClass,
  selected,
  placed,
  stageRef,
  title,
  onPlace,
  onSelect,
}: {
  x: number;
  y: number;
  label: string;
  dotClass: string;
  ringClass: string;
  selected: boolean;
  placed: boolean;
  stageRef: React.RefObject<HTMLDivElement | null>;
  title: string;
  onPlace: (x: number, y: number) => void;
  onSelect: () => void;
}) {
  const [d, setD] = useState<{
    x: number;
    y: number;
    moved: boolean;
    sx: number;
    sy: number;
  } | null>(null);
  const rx = d ? d.x : x;
  const ry = d ? d.y : y;
  return (
    <button
      type="button"
      style={{ left: `${rx}%`, top: `${ry}%` }}
      title={title}
      onPointerDown={(e) => {
        try {
          (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        } catch {
          /* capture is best-effort */
        }
        setD({ x, y, moved: false, sx: e.clientX, sy: e.clientY });
      }}
      onPointerMove={(e) => {
        if (!d) return;
        const rect = stageRef.current?.getBoundingClientRect();
        if (!rect) return;
        const nx = clamp(((e.clientX - rect.left) / rect.width) * 100);
        const ny = clamp(((e.clientY - rect.top) / rect.height) * 100);
        setD({
          ...d,
          x: nx,
          y: ny,
          moved:
            d.moved ||
            Math.abs(e.clientX - d.sx) > 3 ||
            Math.abs(e.clientY - d.sy) > 3,
        });
      }}
      onPointerUp={(e) => {
        try {
          (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
        } catch {
          /* capture is best-effort */
        }
        const cur = d;
        setD(null);
        if (cur && cur.moved) onPlace(cur.x, cur.y);
        else onSelect();
      }}
      className={`absolute z-10 -translate-x-1/2 -translate-y-1/2 grid h-6 w-6 cursor-grab touch-none place-items-center rounded-full text-xs font-bold text-paper shadow-sm ring-4 transition-[transform,box-shadow] ${dotClass} ${
        d
          ? "cursor-grabbing scale-125 ring-sky-300"
          : selected
            ? "scale-125 ring-sky-200"
            : placed
              ? ringClass
              : "ring-paper opacity-80"
      }`}
    >
      {label}
    </button>
  );
}
